import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  forwardRef
} from "@angular/core";
import { Subscription, fromEvent as observableFromEvent } from "rxjs";
import { debounceTime } from "rxjs/operators";

import { OverlayRef, PositionOption } from "@lantern/app/core";

import { OptionsListComponent } from "../base/base-options-list.component";
import { SingleSelectComponent } from "../single/single-select.component";
import { SelectDropdownOptionComponent } from "./select-dropdown-option.component";

export interface SingleSelectWithDropdownComponent<
  Model,
  Identifier extends number | string,
  FieldValue extends Model | Identifier
> extends SingleSelectComponent<Model, Identifier, FieldValue> {
  elementRef: ElementRef;
  open: boolean;
  searchable: boolean;
}

@Component({
  selector: "ta-select-dropdown",
  template: `
    <div (taClickOut)="parent.open = false" #wrapper>
      <div *ngIf="parent.searchable" [className]="selectClasses.header" #header>
        <input
          type="text"
          tabindex="0"
          [className]="selectClasses.search"
          #query
        />
      </div>
      <div
        (scroll)="onOptionsContainerScroll($event)"
        [className]="selectClasses.scrollContainer"
        #scrollContainer
      >
        <ta-select-option
          *ngFor="let option of options"
          [option]="option"
          [className]="selectClasses.option"
        ></ta-select-option>
        <div
          *ngIf="parent.loadingOptions"
          [className]="selectClasses.loadingNotice"
        >
          Loading...
        </div>
        <div
          *ngIf="!parent.loadingOptions && (!options || !options.length)"
          [className]="selectClasses.loadingNotice"
        >
          No options found
        </div>
      </div>
    </div>
  `
})
export class SelectDropdownComponent<Model>
  extends OptionsListComponent<Model>
  implements AfterViewInit, OnInit, OnDestroy
{
  @ViewChild("wrapper", { static: true }) wrapper: ElementRef;
  @ViewChild("header", { static: true }) header: ElementRef;
  @ViewChild("query", { static: false }) queryInput: ElementRef;
  @ViewChild("scrollContainer", { static: true }) scrollContainer: ElementRef;
  @ViewChildren(forwardRef(() => SelectDropdownOptionComponent))
  optionComponents: QueryList<SelectDropdownOptionComponent<Model>>;

  @HostListener("document:keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    super.onKeyDown(event);

    if (event.key === "Enter") {
      if (this.focusedOption.value != null) {
        this.parent.setSelected(this.focusedOption.value);
        this.close();
      }
    }

    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      (this.parent.elementRef.nativeElement as HTMLElement).focus();
    }

    if (event.key === "Tab") {
      this.close();
      // focus parent so tab moves focus to field appearing next on page
      // (dropdown is appended to body, so technically its last / nearly last on page)
      (this.parent.elementRef.nativeElement as HTMLElement).focus();
    }
  }

  constructor(
    @Inject(forwardRef(() => SingleSelectComponent))
    public parent: SingleSelectWithDropdownComponent<Model, any, any>,
    renderer: Renderer2,
    elementRef: ElementRef,
    private overlayRef: OverlayRef
  ) {
    super(parent, renderer, elementRef);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setHostClasses();
    this.setPositionClass(this.overlayRef.currentPosition);
    this.initPositionChangeSubscription();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.setMinWidth();
    this.focusQueryInput();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.positionChangeSubscription) {
      this.positionChangeSubscription.unsubscribe();
    }
    if (this.optionsSubscription) {
      this.optionsSubscription.unsubscribe();
    }
  }

  protected setHostClasses(): void {
    this.renderer.addClass(
      this.wrapper.nativeElement,
      this.selectClasses.optionsList
    );
  }

  protected initOptionsSubscription(): void {
    this.optionsSubscription = this.parent.loadedOptions.subscribe(
      (newOptions) => {
        this.options = newOptions;

        if (this.initializeFocusedOption && newOptions.length > 0) {
          setTimeout(() => {
            this.initializeFocusedOption = false;
            this.setInitialFocusedOption();
            this.updateScrollPos(true);
          });
        }
      }
    );
  }

  protected initQueryInputSubscription(): void {
    if (this.queryInput) {
      this.queryInputSubscription = observableFromEvent(
        this.queryInput.nativeElement,
        "input"
      )
        .pipe(debounceTime(300))
        .subscribe(() => {
          this.initializeFocusedOption = true;
          this.parent.queryString.next(this.queryString);
        });
    }
  }

  private positionChangeSubscription: Subscription;
  private initPositionChangeSubscription(): void {
    this.positionChangeSubscription =
      this.overlayRef.currentPositionChange.subscribe(
        (newPosition: PositionOption) => this.setPositionClass(newPosition)
      );
  }

  private setPositionClass(position?: PositionOption): void {
    if (position === "top-right" || position === "top-left") {
      this.renderer.addClass(
        this.wrapper.nativeElement,
        this.selectClasses.optionsListReversed
      );
      return;
    }
    this.renderer.removeClass(
      this.wrapper.nativeElement,
      this.selectClasses.optionsListReversed
    );
  }

  close(): void {
    this.parent.open = false;
  }

  optionsContainerScroll(event: Event): void {
    let el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight > el.scrollHeight - 10) {
      this.parent.getNextOptionsPage();
    }
  }

  private setMinWidth(): void {
    // doing this here because dropdown isn't child of selectComponent in DOM
    const wrapperEl = this.wrapper.nativeElement as HTMLElement,
      parentRect: ClientRect = (
        this.parent.elementRef.nativeElement as HTMLElement
      ).getBoundingClientRect();

    if (wrapperEl.clientWidth < parentRect.width) {
      wrapperEl.style.minWidth = parentRect.width + "px";
    }
  }

  scrollFocusedOption(steps: number = 1): void {
    if (this.focusedOption.value == null) {
      return;
    }

    let focusIndex = this.options.indexOf(this.focusedOption.value);
    if (steps < 0 && focusIndex + steps >= 0) {
      this.updateFocusedOption(this.options[focusIndex + steps], true);
      return;
    }

    if (steps > 0 && focusIndex + steps < this.options.length) {
      this.updateFocusedOption(this.options[focusIndex + steps], true);
      return;
    }
  }

  updateFocusedOption(option: Model, forceScroll: boolean = false): void {
    this.focusedOption.next(option);

    if (forceScroll) {
      this.updateScrollPos();
    }
  }
}
